
import React from "react";
import showdown from "showdown";
import { CacheManager } from "./CacheManager";
import * as ReactRouterDom from "react-router-dom";
import { SetURLSearchParams } from "react-router-dom";
import { IProfile, SanitizedResponse } from "../services";
import { Button, Classname, MessageSchemesIcons, NoseurObject, Popover, PopoverManageRef, Scheme, Toaster } from "@ronuse/noseur";
import { Debugger } from "./Debugger";
import { LzEncryptor } from "./Encryptor";

export type URLSearchParamsValue = string | string[] | number | number[] | undefined | null;

export const Utils = {

    MARKDOWN_CONVERTER: new showdown.Converter(),

    BUTTON_LOADING_PROPS: {
        disabled: true,
        leftIcon: "fas fa-spinner fa-pulse"
    },

    COLLAPSIBLE_ICONS: {
        expand: "fa fa-plus",
        collapse: "fa fa-minus",
    },

    FA_TO_SVG_MAP: {
        "fa fa-info": (<svg width="25" height="25" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 42C33.0457 42 42 33.0457 42 22C42 10.9543 33.0457 2 22 2C10.9543 2 2 10.9543 2 22C2 33.0457 10.9543 42 22 42Z" stroke="var(--platformColor, #4D1579)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17.3846 17.3847C17.3846 16.4718 17.6553 15.5795 18.1625 14.8205C18.6696 14.0615 19.3904 13.4699 20.2338 13.1206C21.0771 12.7713 22.0051 12.6799 22.9004 12.858C23.7957 13.0361 24.6181 13.4756 25.2636 14.1211C25.9091 14.7666 26.3486 15.589 26.5267 16.4843C26.7048 17.3796 26.6134 18.3076 26.2641 19.1509C25.9148 19.9943 25.3232 20.7151 24.5642 21.2222C23.8052 21.7294 22.9129 22.0001 22 22.0001V25.077" stroke="var(--platformColor, #4D1579)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 29.6923C21.5436 29.6923 21.0974 29.8276 20.7179 30.0812C20.3384 30.3348 20.0426 30.6952 19.868 31.1168C19.6933 31.5385 19.6476 32.0025 19.7367 32.4502C19.8257 32.8978 20.0455 33.309 20.3682 33.6317C20.691 33.9545 21.1022 34.1743 21.5498 34.2633C21.9975 34.3523 22.4615 34.3066 22.8831 34.132C23.3048 33.9573 23.6652 33.6615 23.9188 33.282C24.1724 32.9025 24.3077 32.4564 24.3077 32C24.2997 31.3904 24.054 30.808 23.623 30.377C23.1919 29.9459 22.6096 29.7002 22 29.6923Z" fill="black" />
        </svg>),
        "fa fa-book": (<svg width="25" height="25" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.44445 40C3.22223 40 2.17556 39.5644 1.30445 38.6933C0.433337 37.8222 -0.00147771 36.7763 3.77287e-06 35.5556V4.44445C3.77287e-06 3.22223 0.435559 2.17556 1.30667 1.30445C2.17778 0.433337 3.22371 -0.00147771 4.44445 3.77287e-06H35.5556C36.7778 3.77287e-06 37.8244 0.435559 38.6956 1.30667C39.5667 2.17778 40.0015 3.22371 40 4.44445V35.5556C40 36.7778 39.5644 37.8244 38.6933 38.6956C37.8222 39.5667 36.7763 40.0015 35.5556 40H4.44445ZM4.44445 4.44445V35.5556H35.5556V4.44445H31.1111V20L25.5556 16.6667L20 20V4.44445H4.44445Z" fill="var(--platformColor, #4D1579)" />
        </svg>),
        "fa fa-lifeguard": (<svg width="25" height="25" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 42C33.0457 42 42 33.0457 42 22C42 10.9543 33.0457 2 22 2C10.9543 2 2 10.9543 2 22C2 33.0457 10.9543 42 22 42Z" stroke="var(--platformColor, #4D1579)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 32C27.5228 32 32 27.5228 32 22C32 16.4772 27.5228 12 22 12C16.4772 12 12 16.4772 12 22C12 27.5228 16.4772 32 22 32Z" stroke="var(--platformColor, #4D1579)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M29.5 14.5L35.75 8.25M29.5 29.5L35.75 35.75M14.5 29.5L8.25 35.75M14.5 14.5L8.25 8.25" stroke="var(--platformColor, #4D1579)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>),
    } as { [key: string]: any },

    buildFaSvg: (className: string) => {
        return Utils.FA_TO_SVG_MAP[className] ?? React.createElement("i", { className });
    },

    LOADING_STATE: <div className="loader"><i className="fa fa-spinner fa-spin" /></div>,

    buildProfileDropdown: (platform: string, profile: IProfile, settingButton: React.RefObject<HTMLButtonElement | null>,
        profilePopOverManageRef: React.RefObject<PopoverManageRef | null>, labels: any, openTicketCount?: number) => {
        return (<div className={Classname.build("profile-dropdown", (!profile?.email ? "not-authorized" : null))}>
            <Button leftIcon={profile?.email
                ? (<img alt={profile?.email} src={profile?.profile_picture_location} />)
                : (<i className="fa fa-circle" style={{ marginRight: 10, fontSize: 35 }} />)}
                rightIcon="fa fa-angle-down" ref={settingButton} scheme={Scheme.LIGHT} style={{ fontSize: 20 }}
                onClick={(e: any) => profilePopOverManageRef.current?.toggle(e, settingButton.current!)} textOnly />
            <Popover manageRef={profilePopOverManageRef} className="popover" dismissOnClick>
                {!profile?.external_id ? (<span onClick={Utils.signIn} className={"menu-item"}>{labels.login}</span>) : null}
                {!!platform && labels.menu_items.map((menuItem: any) => (<ReactRouterDom.NavLink key={menuItem.label} to={`/${platform}${menuItem.location}`} className={"menu-item"}>{menuItem.label}</ReactRouterDom.NavLink>))}
                {profile?.external_id ? (<>
                    {!!platform && (<a href={`/${platform}/support/tickets`}>{labels.your_tickets} {openTicketCount && (<span className="badge">{openTicketCount > 9 ? "9+" : openTicketCount}</span>)} </a>)}
                    <span className="danger" onClick={() => Utils.signOut(platform)}>{labels.sign_out}</span>
                </>) : null}
            </Popover>
        </div>);
    },

    collapseString: (value: string, max: number, contd: string = "...") => {
        let collapsedBack = "";
        let collapsedFront = "";
        let halfMax = Math.trunc(max / 2);
        if (!value || value?.length < max) return value;
        for (let index = 0; index < halfMax; index++) {
            collapsedFront += value[index];
            collapsedBack += value[value?.length - halfMax + index];
        }
        return collapsedFront + contd + collapsedBack;
    },

    report: (content: string, scheme: Scheme = Scheme.DANGER, icon: MessageSchemesIcons = MessageSchemesIcons.INFO, lifetime: number = 5000) => {
        Toaster.toast({
            icon,
            scheme,
            content,
            lifetime,
            foreScheme: true,
            showProgressbar: true,
            pauseDelayOnHover: true,
            style: { background: "white" },
        });
    },

    reportSuccess: (content: string, lifetime: number = 5000) => {
        Utils.report(content, "rivtn-hera" as Scheme, MessageSchemesIcons.SUCCESS, lifetime);
    },

    reportError: (response: SanitizedResponse<any> | string) => {
        Utils.report(((typeof response === "string") ? response : (response?.errorMessage ?? `${response}`)));
    },

    signIn: () => {
        window.location = (process.env.REACT_APP_PATULCIUS_AUTH_URL ?? "http://localhost:3005") + "?ref=" + window.location.href + "&bid=" + process.env.REACT_APP_PATULCIUS_BID as any;
    },

    signOut: (platform?: string) => {
        CacheManager.clear();
        window.location = ("/" + (platform ?? "")) as any;
    },

    processSigninData: (encryptedPas: string, onDone: () => void) => {
        const pas = JSON.parse(LzEncryptor.decrypt(encryptedPas, true)) as any;
        Debugger.log("Header:useEffect:pas", encryptedPas, "\n=>\n", pas);
        CacheManager.insecurePut(CacheManager.ACCESS_TOKEN_KEY, pas.a);
        CacheManager.put(CacheManager.SESSION_TOKEN_KEY, pas.s);
        CacheManager.put(CacheManager.PROFILE_KEY, pas.p);
        onDone();
    },

    updateSearchParams(key: string, value: URLSearchParamsValue, setSearchParams: SetURLSearchParams | URLSearchParams, cb?: Function | undefined) {
        const fun = (prev: URLSearchParams) => {
            if (value === undefined || value === null) {
                prev.delete(key);
            } else if (Array.isArray(value)) {
                prev.delete(key);
                for (const v of value) prev.append(key, `${v}`);
            } else {
                prev.set(key, `${value}`);
            }
            return prev;
        };
        if (setSearchParams instanceof URLSearchParams) fun(setSearchParams);
        else setSearchParams(fun);
        cb && cb();
    },

    normalizeUrlParams(urlSearchParams: URLSearchParams, keyLookupMap: NoseurObject<string> = {}, extraParams: NoseurObject<string | number> = {},
        reverseKeyLookupMap: NoseurObject<string> = {}, only?: string[], exclude?: string[]) {
        const query: NoseurObject<any> = {};
        Object.keys(Object.fromEntries(urlSearchParams)).forEach((key: string) => {
            const lookupKey = keyLookupMap[key] ?? key;
            if (only && !only.includes(lookupKey)) return;
            if (exclude && exclude.includes(lookupKey)) return;
            const values = urlSearchParams.getAll(reverseKeyLookupMap[lookupKey] ? lookupKey : key);
            if (values.length) {
                query[reverseKeyLookupMap[lookupKey] ?? lookupKey] = values.length > 1 ? values : values[0];
            }
        });
        Object.keys(extraParams).forEach((key) => query[key] = extraParams[key])
        return query;
    },


    // Tue, June 14th 2023
    formatToFineDate(dateStr: string) {
        const date = new Date(dateStr);
        const month = date.toLocaleString('default', { month: 'long' });
        const weekday = date.toLocaleString('default', { weekday: 'short' });
        const day = date.toLocaleString('default', { day: '2-digit' }).padStart(2, "0");
        return `${weekday}, ${month} ${day} ${date.getFullYear()}`;
    },

    stringSplice: (value: string, index: number, subst: string, endIndex?: number) => {
        if (index < 1) return subst + value;
        return value.substring(0, index) + subst + value.substring((endIndex ?? index), value.length);
    },

    stringCleave: (value: string, fromIndex: number, toIndex: number) => {
        return value.substring(0, fromIndex) + value.substring(toIndex, value.length);
    },

    fileToBase64: (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
                const base64data = reader.result as string;
                if (!base64data) {
                    reject(new Error("Failed to convert File to Base64"));
                    return;
                }
                resolve(base64data!.substring(base64data!.indexOf(',') + 1));
            };
        });
    },

    hexToRgb: (hex: string) => {
        let c: any;
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            c = hex.substring(1).split('');
            if (c.length == 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = '0x' + c.join('');
            return [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',');
        }
        throw "77, 21, 121";
    },

    loadStyle: (styleString: string) => {
        const style = document.createElement('style');
        style.textContent = styleString;
        document.head.append(style);
    },

    createAbsoluteUrl: (prefix: string, url: string) => {
        if (url.startsWith("http")) return url;
        return (prefix + url);
    },

    updateMeta: (meta: { title: string; desc: string; logo?: string; }) => {
        if (!window.document) return;
        document.title = meta.title + " Support - " + meta.desc + " - Rivtn Support (Hera)";
        if (meta.logo) (window.document.getElementById("__oh-uls-icon__") as any).href = meta.logo;
    },

}

export const Schemes: {
    RIVTN_HERA: Scheme;
} = {
    RIVTN_HERA: "rivtn-hera" as Scheme
}

export * from "./Debugger";
export * from "./Encryptor";
export * from "./CacheManager";